import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAutoUpdate } from '@/hooks/useAutoUpdate'

/**
 * Story E01.12 — full-screen "new version available" overlay (legacy
 * api.js:1102-1183 parity, architecture §13.3).
 *
 * Visibility is driven by `useAutoUpdate().pending`. When non-null we
 * render a fixed full-screen scrim, count down 5 → 0, then trigger
 * `reload(version)` (see hook for cache-bust + replace semantics).
 *
 * Why z-index 999_999: legacy CSS reserves the band 9000-99999 for
 * dialogs and toasts; the auto-update overlay must sit above all of
 * them so the user can't dismiss it by clicking through. Defined
 * inline (rather than via Tailwind config) because no other UI lives
 * at that altitude.
 */
const COUNTDOWN_SECONDS = 5

export function AutoUpdateOverlay() {
  const { pending, reload } = useAutoUpdate()
  if (!pending) return null
  return <CountdownOverlay version={pending} onReload={reload} />
}

/**
 * Split out so the timer effect remounts cleanly per pending version.
 * Mounting `<CountdownOverlay key={pending}>` ensures we never carry a
 * partially-elapsed countdown across version flips (rare, but possible
 * if two deploys hit within a poll window).
 */
function CountdownOverlay({
  version,
  onReload,
}: {
  version: string
  onReload: (v: string) => void
}) {
  const { t } = useTranslation()
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS)

  useEffect(() => {
    if (remaining <= 0) {
      onReload(version)
      return
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000)
    return () => clearTimeout(id)
  }, [remaining, version, onReload])

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-black/80 text-white"
      style={{ zIndex: 999_999 }}
    >
      <h2 className="text-2xl font-semibold">{t('autoUpdate.title')}</h2>
      <p className="text-base">{t('autoUpdate.countdown', { n: remaining })}</p>
    </div>
  )
}
